import 'react-datepicker/dist/react-datepicker.css';
import { forwardRef } from 'react';
import { colors, fontSizes, fontWeights } from '@anyroad/theme';
import { css } from '@emotion/react';
import DatePicker from 'react-datepicker';
import { Icon } from '@anyroad/icon';
import styled from '@emotion/styled';

export const getMonthList = (
  lang?: string[] | string,
  format?: 'long' | 'short'
): string[] =>
  Array.from(Array(12).keys(), (i) =>
    new Intl.DateTimeFormat(lang, {
      month: format || 'long',
    }).format(new Date(0, i))
  );

type DateSelectProps = {
  availableDates?: Date[];
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  onChange: (date: Date) => void;
  selected?: Date;
  brandColor: string;
};

const DateContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  fontSize: '16px;',
});

const DatePickerStyle = styled(DatePicker)`
  background-color: ${colors.white};
  border: 1px solid ${colors.borderAction};
  color: ${colors.text.primary};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.regular};
  font-family: 'proxima-nova';
  line-height: 18px;
  padding: 10px 0 8px 16px;
  width: 100%;
  word-spacing: 2px;
`;

const CalendarIcon = styled(Icon)({
  bottom: '3px',
  fill: `${colors.text.icon}`,
  height: '24px',
  right: '3px',
  width: '24px',
});

const MonthTitleStyle = styled.span({
  color: `${colors.text.primary}`,
  fontSize: `${fontSizes.large}`,
  fontWeight: 700,
  lineHeight: '24px',
});

const ArrowNextButton = styled(Icon)({
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  transform: 'rotate(180deg)',
});

const ArrowPreviousButton = styled(Icon)({
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  marginLeft: '2px',
  position: 'relative',
  right: '8px',
});

const HeaderStyle = styled.div({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-around',
  marginBottom: '10px',
  background: 'white',
});

const DatePackageCSSOverwrite = css`
  .react-datepicker {
    font-size: 14px;
  }
  .react-datepicker__header {
    background-color: ${colors.white};
    border-bottom: none;
    padding: 8px 0 0 0;
  }
  .react-datepicker__day-name {
    font-weight: 500;
    text-decoration: underline dotted;
    font-weight: 700;
  }
  .react-datepicker__day,
  .react-datepicker__day-name {
    margin: 0 5.5px;
    font-size: 12px;
  }
  .react-datepicker__day-names {
    margin-bottom: 8px;
  }
  .react-datepicker__day--outside-month {
    color: ${colors.white};
    background-color: ${colors.white};
    border: 1px solid ${colors.white} !important;
    &:hover {
      background-color: ${colors.white};
      color: ${colors.white};
    }
  }
`;

export const Calendar = forwardRef<HTMLDivElement, DateSelectProps>(
  (
    {
      availableDates,
      className,
      dataTestId,
      disabled,
      selected,
      onChange,
      brandColor,
    },
    ref
  ) => {
    const currentYear = new Date().getFullYear();

    return (
      <DateContainer
        css={DatePackageCSSOverwrite}
        data-testid={dataTestId}
        ref={ref}
      >
        <DatePickerStyle
          className={className}
          calendarStartDay={1}
          data-testid="date-select"
          dateFormat="MMMM d, yyyy"
          disabled={disabled}
          showIcon={!disabled}
          // @ts-ignore
          icon={<CalendarIcon name="Bookings" />}
          includeDates={availableDates}
          minDate={new Date()}
          onChange={(date: Date) => onChange(date)}
          placeholderText="Select"
          popperPlacement="bottom-end"
          renderCustomHeader={({ date, decreaseMonth, increaseMonth }: any) => {
            const getMonths = getMonthList('en', 'long');
            const month = getMonths[date.getMonth()];
            return (
              <HeaderStyle>
                <ArrowPreviousButton
                  name="ThinArrow"
                  fill={brandColor}
                  onClick={decreaseMonth}
                />
                <MonthTitleStyle>
                  {month} {currentYear}
                </MonthTitleStyle>
                <ArrowNextButton
                  name="ThinArrow"
                  fill={brandColor}
                  onClick={increaseMonth}
                />
              </HeaderStyle>
            );
          }}
          selected={selected}
          useWeekdaysShort
        />
      </DateContainer>
    );
  }
);

Calendar.displayName = 'Calendar';
